import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import BottomSheet from "./BottomSheet";
import {
  DATE_PRESETS,
  datePresetToRange,
  endOfDayMs,
  startOfDayMs,
} from "./partnersMobileUtils";
import css from "./partnersMobile.module.css";

// CP filter only (desktop has no FOS filter). Must match allStatusDropDown /
// allTypeDropDown in ChannelPartner.jsx.
const STATUS_OPTIONS = ["pending", "approved", "rejected", "resubmitted"];
const TYPE_OPTIONS = ["Entity", "Individual"];

const controlSx = { "& .MuiSvgIcon-root": { fontSize: 22 } };
const labelSx = {
  margin: 0,
  minHeight: 44,
  "& .MuiFormControlLabel-label": {
    fontFamily: "Inter-Medium",
    fontSize: 15,
    color: "#101828",
    textTransform: "capitalize",
  },
};

const dateInputStyle = {
  display: "block",
  width: "100%",
  minHeight: 44,
  marginTop: 6,
  padding: "10px 12px",
  border: "1px solid #D0D5DD",
  borderRadius: 10,
  fontFamily: "Inter-Medium",
  fontSize: 15,
};

// Local calendar date, not toISOString: the applied start is local midnight,
// which in IST is the previous day in UTC -- so the picker reopened one day
// early and every re-apply shifted the range back another day.
function toDateInputValue(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const dateErrorStyle = { display: "block", marginTop: 4, fontSize: 12, color: "#B42318" };

export default function FilterSheet({
  open,
  statuses,
  types,
  datePreset,
  customRange,
  onApply,
  onClearAll,
  onClose,
}) {
  // Staged copies. Re-seeded from the committed props every time the sheet
  // opens, so a discarded edit never leaks into the next session.
  const [draftStatuses, setDraftStatuses] = useState(statuses ?? []);
  const [draftTypes, setDraftTypes] = useState(types ?? []);
  const [draftPreset, setDraftPreset] = useState(datePreset ?? "all");
  const [draftStart, setDraftStart] = useState(toDateInputValue(customRange?.start));
  const [draftEnd, setDraftEnd] = useState(toDateInputValue(customRange?.end));
  const [dateError, setDateError] = useState({ start: "", end: "" });

  useEffect(() => {
    if (!open) return;
    setDraftStatuses(statuses ?? []);
    setDraftTypes(types ?? []);
    setDraftPreset(datePreset ?? "all");
    setDraftStart(toDateInputValue(customRange?.start));
    setDraftEnd(toDateInputValue(customRange?.end));
    setDateError({ start: "", end: "" });
    // Seeding is intentionally keyed on `open` alone — re-seeding on every prop
    // change would wipe the user's in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const handleApply = () => {
    // Date.parse("YYYY-MM-DD") is UTC midnight, which would both shift the day
    // and clip the end date. Re-anchor to local whole-day bounds, as desktop does.
    const startMs = draftStart
      ? startOfDayMs(new Date(`${draftStart}T00:00:00`).getTime())
      : null;
    const endMs = draftEnd
      ? endOfDayMs(new Date(`${draftEnd}T00:00:00`).getTime())
      : null;

    let preset = draftPreset;
    if (preset === "custom") {
      // Same guards the desktop date filter applies before it fetches.
      if (!startMs && endMs) {
        setDateError({ start: "", end: "Please select start date first" });
        return;
      }
      if (startMs && !endMs) {
        setDateError({ start: "", end: "Please Select end Date" });
        return;
      }
      if (startMs && endMs && endMs < startMs) {
        setDateError({ start: "", end: "Select End Date Greater than start Date" });
        return;
      }
      // No dates picked means no date constraint, as on desktop -- don't leave
      // a "Custom range" tag on screen that filters nothing.
      if (!startMs && !endMs) preset = "all";
    }

    const range =
      preset === "custom"
        ? { start: startMs, end: endMs }
        : datePresetToRange(preset, Date.now());

    onApply({
      statuses: draftStatuses,
      types: draftTypes,
      datePreset: preset,
      range,
    });
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      title="Filters"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className={`${css.btn} ${css.btnSecondary}`}
            onClick={() => {
              onClearAll();
              onClose();
            }}
          >
            Clear all
          </button>
          <button
            type="button"
            className={`${css.btn} ${css.btnPrimary}`}
            onClick={handleApply}
          >
            Apply filters
          </button>
        </>
      }
    >
      <div className={css.sheetGroupTitle}>Status</div>
      {STATUS_OPTIONS.map((status) => (
        <FormControlLabel
          key={status}
          sx={labelSx}
          control={
            <Checkbox
              sx={controlSx}
              checked={draftStatuses.includes(status)}
              onChange={() => toggle(draftStatuses, setDraftStatuses, status)}
            />
          }
          label={status}
        />
      ))}

      <div className={css.sheetGroupTitle}>Partner type</div>
      {TYPE_OPTIONS.map((type) => (
        <FormControlLabel
          key={type}
          sx={labelSx}
          control={
            <Checkbox
              sx={controlSx}
              checked={draftTypes.includes(type)}
              onChange={() => toggle(draftTypes, setDraftTypes, type)}
            />
          }
          label={type}
        />
      ))}

      <div className={css.sheetGroupTitle}>Date created</div>
      {DATE_PRESETS.map((preset) => (
        <FormControlLabel
          key={preset.value}
          sx={labelSx}
          control={
            <Radio
              sx={controlSx}
              checked={draftPreset === preset.value}
              onChange={() => setDraftPreset(preset.value)}
            />
          }
          label={preset.label}
        />
      ))}

      {draftPreset === "custom" && (
        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <label style={{ flex: 1, fontSize: 13, color: "#667085" }}>
            Start date
            <input
              type="date"
              aria-label="Start date"
              value={draftStart}
              onChange={(e) => {
                setDraftStart(e.target.value);
                setDateError({ start: "", end: "" });
              }}
              style={dateInputStyle}
            />
            {dateError.start && <span style={dateErrorStyle}>{dateError.start}</span>}
          </label>
          <label style={{ flex: 1, fontSize: 13, color: "#667085" }}>
            End date
            <input
              type="date"
              aria-label="End date"
              value={draftEnd}
              min={draftStart || undefined}
              onChange={(e) => {
                setDraftEnd(e.target.value);
                setDateError({ start: "", end: "" });
              }}
              style={dateInputStyle}
            />
            {dateError.end && <span style={dateErrorStyle}>{dateError.end}</span>}
          </label>
        </div>
      )}
    </BottomSheet>
  );
}
